import React, { useEffect, useRef, useState } from "react";
import "./portfolio.css";
import "swiper/css";
import "swiper/css/pagination";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import { motion } from "framer-motion";
import { Icon } from "@iconify/react/dist/iconify.js";

// Cover thumbnails (always visible — kept small and static)
import amazonCover from "../../img/GifsCover/amazonCover.jpg";
import bookingCover from "../../img/GifsCover/bookingCover.jpg";
import chatCover from "../../img/GifsCover/chatCover.jpg";
import calendarCover from "../../img/GifsCover/calendarCover.jpg";
import notesCover from "../../img/GifsCover/notesCover.png";
import restCover from "../../img/GifsCover/restCover.jpg";
import qrcodeCover from "../../img/GifsCover/qrcodeCover.jpg";
import movieCover from "../../img/GifsCover/movieCover.jpg";
import expenseCover from "../../img/GifsCover/expenseCover.jpg";
import todoCover from "../../img/GifsCover/todoCover.jpg";
import fooddeliveryCover from "../../img/GifsCover/fooddeliveryCover.jpg";
import blogCover from "../../img/GifsCover/blogCover.jpg";
import memeCover from "../../img/GifsCover/memeCover.jpg";
import airbnbCover from "../../img/GifsCover/airbnbCover.jpg";
import neurochatCover from "../../img/GifsCover/neurochatCover.png";
import codelensCover from "../../img/GifsCover/codelensCover.jpg";
import docmindCover from "../../img/GifsCover/docmindCover.jpg";
import resumeforgeCover from "../../img/GifsCover/resumeforgeCover.jpg";

// Gallery clips — encoded as small muted MP4s (a few hundred KB each)
// instead of multi-megabyte animated GIFs, and only mounted in the DOM
// (so only then actually fetched) when their slide is active or adjacent.
import Amazon1 from "../../img/Gifs/Amazon/Amazon1.mp4";
import Amazon2 from "../../img/Gifs/Amazon/Amazon2.mp4";
import Amazon3 from "../../img/Gifs/Amazon/Amazon3.mp4";
import Amazon4 from "../../img/Gifs/Amazon/Amazon4.mp4";
import Amazon5 from "../../img/Gifs/Amazon/Amazon5.mp4";
import Amazon6 from "../../img/Gifs/Amazon/Amazon6.mp4";
import Amazon7 from "../../img/Gifs/Amazon/Amazon7.mp4";
import Amazon8 from "../../img/Gifs/Amazon/Amazon8.mp4";
import Booking0 from "../../img/Gifs/Booking/Booking0.mp4";
import Booking1 from "../../img/Gifs/Booking/Booking1.mp4";
import Booking2 from "../../img/Gifs/Booking/Booking2.mp4";
import Booking3 from "../../img/Gifs/Booking/Booking3.mp4";
import Booking4 from "../../img/Gifs/Booking/Booking4.mp4";
import Booking5 from "../../img/Gifs/Booking/Booking5.mp4";
import Booking6 from "../../img/Gifs/Booking/Booking6.mp4";
import ChatProject1 from "../../img/Gifs/Chat/ChatProject1.mp4";
import ChatProject2 from "../../img/Gifs/Chat/ChatProject2.mp4";
import ChatProject3 from "../../img/Gifs/Chat/ChatProject3.mp4";
import ChatProject4 from "../../img/Gifs/Chat/ChatProject4.mp4";
import Calendar1 from "../../img/Gifs/Calendar/Calendar1.mp4";
import Calendar2 from "../../img/Gifs/Calendar/Calendar2.mp4";
import NotesProject1 from "../../img/Gifs/Notes/NotesProject1.mp4";
import NotesProject2 from "../../img/Gifs/Notes/NotesProject2.mp4";
import NotesProject3 from "../../img/Gifs/Notes/NotesProject3.mp4";
import Restaurant from "../../img/Gifs/Restaurant/Restaurant.mp4";
import QR from "../../img/Gifs/QR/QR.mp4";
import movies from "../../img/Gifs/Movies/movies.jpg";
import Expenses from "../../img/Gifs/Expenses/Expenses.mp4";
import Todo from "../../img/Gifs/todo/Todo.mp4";
import Food1 from "../../img/Gifs/food/Food1.mp4";
import Food2 from "../../img/Gifs/food/Food2.mp4";
import blog from "../../img/Gifs/Blog/blog.mp4";
import blog2 from "../../img/Gifs/Blog/blog2.mp4";
import blog3 from "../../img/Gifs/Blog/blog3.mp4";
import memeGenerator from "../../img/Gifs/meme/memeGenerator.mp4";
import airbnb1 from "../../img/Gifs/airbnb/airbnb1.jpg";
import airbnb2 from "../../img/Gifs/airbnb/airbnb2.jpg";
import NeuroChat1 from "../../img/Gifs/NeuroChat/NeuroChat1.mp4";
import CodeLens1 from "../../img/Gifs/CodeLens/CodeLens1.mp4";
import DocMind1 from "../../img/Gifs/DocMind/DocMind1.mp4";
import ResumeForge1 from "../../img/Gifs/ResumeForge/ResumeForge1.mp4";

const video = (src) => ({ type: "video", src });
const image = (src) => ({ type: "image", src });

const projects = [
  {
    title: "NeuroChat",
    tag: "AI",
    tagline:
      "Streaming AI chat assistant with switchable personas, powered by the Claude API.",
    link: "https://github.com/devMoAli/NeuroChat-AI-Chat-Assistant",
    cover: neurochatCover,
    gallery: [NeuroChat1].map(video),
  },
  {
    title: "CodeLens",
    tag: "AI",
    tagline:
      "AI code reviewer that rates quality, flags bugs, and suggests concrete fixes.",
    link: "https://github.com/devMoAli/CodeLens-AI-Code-Reviewer",
    cover: codelensCover,
    gallery: [CodeLens1].map(video),
  },
  {
    title: "E-Commerce Project",
    tag: "E-Commerce",
    tagline:
      "Full-featured online store with cart, checkout, and order management.",
    link: "https://github.com/devMoAli/Amazon-Mern-Stack-E-Commerce-Project",
    cover: amazonCover,
    gallery: [
      Amazon1,
      Amazon2,
      Amazon3,
      Amazon4,
      Amazon5,
      Amazon6,
      Amazon7,
      Amazon8,
    ].map(video),
  },
  {
    title: "ResumeForge",
    tag: "AI",
    tagline:
      "AI resume optimizer with ATS scoring, keyword gaps, and a targeted rewrite.",
    link: "https://github.com/devMoAli/ResumeForge-AI-Resume-Optimizer",
    cover: resumeforgeCover,
    gallery: [ResumeForge1].map(video),
  },

  {
    title: "Booking Project",
    tag: "Booking",
    tagline:
      "Hotel booking platform with real-time availability and reservations.",
    link: "https://github.com/devMoAli/Booking-Mern-Stack-Project",
    cover: bookingCover,
    gallery: [
      Booking0,
      Booking1,
      Booking2,
      Booking3,
      Booking4,
      Booking5,
      Booking6,
    ].map(video),
  },
  {
    title: "Chat Project",
    tag: "Real-Time",
    tagline: "Live messaging app with real-time chat and notifications.",
    link: "https://github.com/devMoAli/Chat_Mern_Stack_Project",
    cover: chatCover,
    gallery: [ChatProject1, ChatProject2, ChatProject3, ChatProject4].map(
      video,
    ),
  },
  {
    title: "Calendar Events Project",
    tag: "Productivity",
    tagline: "Google Calendar integration for scheduling and managing events.",
    link: "https://github.com/devMoAli/Google-Calendar-Events-Project",
    cover: calendarCover,
    gallery: [Calendar1, Calendar2].map(video),
  },
  {
    title: "Notes Project",
    tag: "Productivity",
    tagline: "Note-taking app with rich text editing and organization.",
    link: "https://github.com/devMoAli/Notes-Mern-Stack-Project",
    cover: notesCover,
    gallery: [NotesProject1, NotesProject2, NotesProject3].map(video),
  },
  {
    title: "Restaurant Project",
    tag: "Hospitality",
    tagline: "Restaurant site with menu browsing and table reservations.",
    link: "https://github.com/devMoAli/Restaurant-Mern-Stack-Project",
    cover: restCover,
    gallery: [Restaurant].map(video),
  },
  {
    title: "QR Code Generator",
    tag: "Utility",
    tagline: "Generate and download custom QR codes instantly.",
    link: "https://github.com/devMoAli/QR-Code-Generator-Project",
    cover: qrcodeCover,
    gallery: [QR].map(video),
  },
  {
    title: "Movies Database",
    tag: "Media",
    tagline: "Searchable movie database with details and ratings.",
    link: "https://github.com/devMoAli/Movies-Database-Project",
    cover: movieCover,
    gallery: [movies].map(image),
  },
  {
    title: "Expense Tracker",
    tag: "Finance",
    tagline: "Track spending and visualize budgets with interactive charts.",
    link: "https://github.com/devMoAli/Expense-Tracker-Project",
    cover: expenseCover,
    gallery: [Expenses].map(video),
  },
  {
    title: "Todo List",
    tag: "Productivity",
    tagline: "Simple, fast task manager for daily to-dos.",
    link: "https://github.com/devMoAli/todo-app-MERN",
    cover: todoCover,
    gallery: [Todo].map(video),
  },
  {
    title: "Food Delivery",
    tag: "E-Commerce",
    tagline: "Order food online with live delivery tracking.",
    link: "https://github.com/devMoAli/food-delivery-MERN",
    cover: fooddeliveryCover,
    gallery: [Food1, Food2].map(video),
  },
  {
    title: "Blog Project",
    tag: "Content",
    tagline: "Full-featured blogging platform with posts and comments.",
    link: "https://github.com/devMoAli/Blog-Mern-Stack-Project",
    cover: blogCover,
    gallery: [blog, blog2, blog3].map(video),
  },
  {
    title: "Meme Generator",
    tag: "Fun",
    tagline: "Create and customize memes with text overlays.",
    link: "https://github.com/devMoAli/meme-generator",
    cover: memeCover,
    gallery: [memeGenerator].map(video),
  },
  {
    title: "Airbnb Clone",
    tag: "Booking",
    tagline: "Property rental platform with listings and booking flow.",
    link: "https://github.com/devMoAli/airbnb-clone",
    cover: airbnbCover,
    gallery: [airbnb1, airbnb2].map(image),
  },
  {
    title: "DocMind",
    tag: "AI",
    tagline:
      "AI document assistant — instant summaries and Q&A grounded in your own text.",
    link: "https://github.com/devMoAli/DocMind-AI-Document-Assistant",
    cover: docmindCover,
    gallery: [DocMind1].map(video),
  },
];

// Only ever mounts (and therefore only ever fetches) its <video> element
// while the slide is active or immediately adjacent, and plays only the
// exactly-active one. Every other slide's clip never touches the network.
const LazyClip = ({ src, isActive, shouldMount, title, index }) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (isActive) {
      const playPromise = el.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
    } else {
      el.pause();
    }
  }, [isActive, shouldMount]);

  if (!shouldMount) {
    return <div className="slide-placeholder" aria-hidden="true" />;
  }

  return (
    <video
      ref={ref}
      src={src}
      muted
      loop
      playsInline
      preload="none"
      aria-label={`${title} demo clip ${index + 1}`}
    />
  );
};

// Repositories are private, so "Request Code" takes the visitor to the
// contact form with the subject pre-filled for that project.
const requestCode = (event, title) => {
  event.preventDefault();
  const contact = document.getElementById("contact");
  const subject = document.getElementById("subject");
  if (subject) subject.value = `Code request: ${title}`;
  if (contact) contact.scrollIntoView({ behavior: "smooth", block: "start" });
  const name = document.getElementById("user_name");
  if (name) setTimeout(() => name.focus({ preventScroll: true }), 600);
};

const ProjectCard = ({ title, tag, tagline, cover, gallery }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const slides = [image(cover), ...gallery];

  return (
    <div className="portfolio-card">
      <div className="portfolio-media">
        <span className="portfolio-tag">{tag}</span>
        <Swiper
          pagination={{ clickable: true }}
          slidesPerView={1}
          modules={[Pagination]}
          className="nested-swiper"
          onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
        >
          {slides.map((slide, i) =>
            slide.type === "video" ? (
              <SwiperSlide key={i}>
                <LazyClip
                  src={slide.src}
                  title={title}
                  index={i}
                  isActive={activeIndex === i}
                  shouldMount={Math.abs(activeIndex - i) <= 1}
                />
              </SwiperSlide>
            ) : (
              <SwiperSlide key={i}>
                <img
                  loading="lazy"
                  src={slide.src}
                  alt={i === 0 ? `${title} cover` : `${title} preview ${i + 1}`}
                />
              </SwiperSlide>
            ),
          )}
        </Swiper>
      </div>

      <div className="portfolio-body">
        <h3 className="projectTitle">{title}</h3>
        <p className="projectTagline">{tagline}</p>
        <a
          className="projectLink"
          href="#contact"
          onClick={(e) => requestCode(e, title)}
        >
          <Icon icon="mdi:lock-outline" width="1.1rem" height="1.1rem" />
          Request Code
        </a>
      </div>
    </div>
  );
};

const Portfolio = () => {
  const transition = { duration: 1, type: "spring" };

  return (
    <div className="portfolio">
      <motion.div
        className="portfolio-heading"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={transition}
      >
        <h2 className="p-heading">Recent Projects</h2>
        <span className="projectDetails">
          A selection of full-stack, e-commerce, and AI-powered builds — each
          shipped from idea to production.
        </span>
      </motion.div>
      <div className="portfolio-cards">
        {projects.map((project) => (
          <ProjectCard key={project.title} {...project} />
        ))}
      </div>
    </div>
  );
};

export default Portfolio;
